import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Coins, RotateCw, Trash2, Spade, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, GamblingNotice } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";
import RouletteWheel, { REDS, ROULETTE_WHEEL, numColor } from "@/components/cef/casino/RouletteWheel";
import SlotsCabinet, { PAYTABLE, SLOT_SYMBOLS, SYM_H } from "@/components/cef/casino/SlotsCabinet";

/* THE GILDED SPADE — in-game casino (CEF) — /cef/casino/:serverId?token=<session>.
   European single-zero roulette (animated wheel, full betting layout) + 3-reel slots.
   Live mode settles every spin server-side against the player's bank (hubcasino.py);
   no token → demo floor with the same physics, funny money. */

const GOLD = "#d4af37";
const FELT = "#0d2b1f";
const WHEEL_ORDER = ROULETTE_WHEEL;
const CHIPS = [10, 25, 50, 100, 500];
const ROOM_CHIPS = { main: [10, 25, 50, 100, 500], high_roller: [500, 1000, 5000, 10000, 25000] };
const ROOM_LABEL = { main: "Main floor", high_roller: "High-roller room" };
const SUIT_GLYPH = { S: "♠", H: "♥", D: "♦", C: "♣" };
const RANKS_SET = new Set(["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"]);
const STEP = 360 / 37;

function Card({ code, hidden, i = 0 }) {
  if (hidden || code === "??") {
    return <div className="w-14 h-20 rounded-md border border-[#d4af3766] shadow-lg" style={{ background: "repeating-linear-gradient(45deg,#2a1a0c 0 3px,#140a04 3px 6px)", animation: `ncCardIn 0.3s ease ${i * 0.08}s both` }} />;
  }
  const rank = code.slice(0, -1);
  const suit = code.slice(-1);
  const red = suit === "H" || suit === "D";
  return (
    <div className="w-14 h-20 rounded-md bg-zinc-50 flex flex-col items-start justify-between p-1.5 shadow-lg" style={{ animation: `ncCardIn 0.3s ease ${i * 0.08}s both` }}>
      <span className={`font-mono font-bold text-sm ${red ? "text-red-600" : "text-zinc-900"}`}>{rank}</span>
      <span className={`text-2xl self-center leading-none ${red ? "text-red-600" : "text-zinc-900"}`}>{SUIT_GLYPH[suit]}</span>
    </div>
  );
}

function handVal(cards) {
  let total = 0, aces = 0;
  for (const c of cards) {
    const r = c.slice(0, -1);
    if (!RANKS_SET.has(r)) continue; // hidden hole card ("??") or malformed
    if (r === "A") { aces++; total += 11; }
    else if (["J", "Q", "K"].includes(r)) total += 10;
    else total += parseInt(r);
  }
  while (total > 21 && aces) { total -= 10; aces--; }
  return total;
}

const fmt = (n) => `$${Number(n).toLocaleString()}`;

export default function CasinoCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const sfx = usePhoneAudio();

  /* transparent CEF chrome — the game world stays visible behind the rail */
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  /* slot machine audio — tiny WebAudio synth (no assets): lever clunk, spin
     ratchet, reel-stop thuds, win arpeggio, jackpot fanfare, coin cascade */
  const slotAudio = useMemo(() => {
    let ctx = null;
    const ac = () => {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === "suspended") ctx.resume();
      return ctx;
    };
    const tone = (freq, at, dur, type = "square", vol = 0.05) => {
      try {
        const c = ac(), o = c.createOscillator(), g = c.createGain();
        o.type = type; o.frequency.value = freq;
        g.gain.setValueAtTime(vol, at);
        g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        o.connect(g).connect(c.destination);
        o.start(at); o.stop(at + dur + 0.02);
      } catch { /* audio unavailable */ }
    };
    const now = () => ac().currentTime;
    return {
      lever() { const t = now(); tone(150, t, 0.07, "square", 0.09); tone(95, t + 0.08, 0.14, "sine", 0.12); },
      ratchet() { const t = now(); for (let i = 0; i < 14; i++) tone(1200 + i * 60, t + i * 0.045, 0.03, "square", 0.022); },
      reelStop() { const t = now(); tone(220, t, 0.05, "triangle", 0.1); tone(110, t + 0.03, 0.1, "sine", 0.12); },
      win() { const t = now(); [523, 659, 784].forEach((f, i) => tone(f, t + i * 0.09, 0.16, "triangle", 0.07)); },
      jackpot() { const t = now(); [523, 659, 784, 1047, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, t + i * 0.11, 0.2, "triangle", 0.08)); [261, 329, 392].forEach((f) => tone(f, t, 0.9, "sawtooth", 0.02)); },
      coins() { const t = now(); for (let i = 0; i < 12; i++) tone(2200 + Math.random() * 1400, t + 0.6 + i * 0.07, 0.05, "square", 0.028); },
    };
  }, []);

  const [game, setGame] = useState("roulette");
  const [room, setRoom] = useState("main");
  const [bank, setBank] = useState(demo ? 5000 : null);
  const [name, setName] = useState("");
  const [recent, setRecent] = useState([]);

  // blackjack state
  const [bj, setBj] = useState(null); // public hand object
  const [bjBet, setBjBet] = useState(50);
  const [bjBusy, setBjBusy] = useState(false);
  const bjDeck = useRef([]);

  // roulette state
  const [bets, setBets] = useState({}); // key -> amount
  const [chip, setChip] = useState(25);
  const [rotation, setRotation] = useState(0);
  const [ballRot, setBallRot] = useState(0);
  const [ballIn, setBallIn] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rouletteResult, setRouletteResult] = useState(null); // {result, color, payout, net}

  // slots state
  const [slotBet, setSlotBet] = useState(25);
  const [reels, setReels] = useState(["seven", "diamond", "bell"]);
  const [reelPos, setReelPos] = useState([0, 0, 0]);
  const [reelSpinning, setReelSpinning] = useState([false, false, false]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [slotWin, setSlotWin] = useState(null);
  const [jackpotFlash, setJackpotFlash] = useState(false);
  const [payoutCount, setPayoutCount] = useState(0);
  const [leverPulled, setLeverPulled] = useState(false);

  // win payout counts up like a real machine meter; jackpot strobes the cabinet
  useEffect(() => {
    if (!slotWin) return;
    const target = slotWin.payout;
    let v = 0;
    const step = Math.max(1, Math.round(target / 24));
    setPayoutCount(0);
    const iv = setInterval(() => {
      v = Math.min(target, v + step);
      setPayoutCount(v);
      if (v >= target) clearInterval(iv);
    }, 45);
    let t;
    if (slotWin.win_kind === "jackpot") {
      setJackpotFlash(true);
      t = setTimeout(() => setJackpotFlash(false), 2800);
    }
    return () => { clearInterval(iv); if (t) clearTimeout(t); };
  }, [slotWin]); // {payout, win_kind}

  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/casino/state`, { params: { token } })
      .then((r) => { setBank(r.data.bank); setName(r.data.name); setPc(r.data.playercode); setRecent(r.data.recent || []); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(load, [load]);

  const stake = Object.values(bets).reduce((a, b) => a + b, 0);
  const roomChips = ROOM_CHIPS[room];

  /* ---- blackjack ---- */
  const bjApplyResult = (h) => {
    setBj(h);
    if (h.bank != null) setBank(h.bank);
    if (h.status === "settled") {
      if (h.result === "blackjack") { sfx.success(); toast.success(`BLACKJACK — ${fmt(h.payout)}`); }
      else if (h.result === "win") { sfx.success(); toast.success(`Winner — ${fmt(h.payout)}`); }
      else if (h.result === "push") toast.info("Push — stake returned");
      else toast.error(h.player_value > 21 ? `Bust at ${h.player_value} — house takes it` : `Dealer takes it`);
      setRecent((rs) => [{ id: `bj-${Date.now()}-${Math.floor(Math.random() * 1e5)}`, kind: "blackjack", result: h.result, payout: h.payout, bet: h.bet * (h.doubled ? 2 : 1), name }, ...rs].slice(0, 12));
    }
  };

  const bjDeal = async () => {
    if (bjBusy || (bj && bj.status === "active")) return;
    if (bjBet > bank) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    setBjBusy(true);
    if (demo) {
      const R = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"], S = ["S", "H", "D", "C"];
      bjDeck.current = R.flatMap((r) => S.map((s) => r + s)).sort(() => Math.random() - 0.5);
      const d = bjDeck.current;
      const hand = { id: "demo-bj", player: [d.pop(), d.pop()], dealer: [d.pop(), "??"], _dealerHole: null, player_value: 0, dealer_value: 0, bet: bjBet, doubled: false, status: "active", room };
      hand._dealerHole = hand.dealer[1]; hand.dealer[1] = "??";
      hand.player_value = handVal(hand.player);
      hand.dealer_value = handVal([hand.dealer[0]]);
      setBank((b) => b - bjBet);
      // naturals settle immediately
      if (hand.player_value === 21) {
        const real = [hand.dealer[0], hand._dealerHole];
        const dv = handVal(real);
        hand.dealer = real;
        bjDemoSettle(hand, dv === 21 ? "push" : "blackjack");
        setBjBusy(false);
        return;
      }
      bjApplyResult({ ...hand });
      setBjBusy(false);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/deal`, { bet: bjBet, room }, { params: { token } });
      bjApplyResult(r.data.hand);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBjBusy(false); }
  };

  const bjDemoSettle = (hand, result) => {
    const bet = hand.bet * (hand.doubled ? 2 : 1);
    const mult = { blackjack: 2.5, win: 2, push: 1, lose: 0 }[result];
    const payout = Math.floor(bet * mult);
    setBank((b) => {
      const nb = b + payout;
      bjApplyResult({ ...hand, status: "settled", result, payout, bank: nb, dealer_value: handVal(hand.dealer) });
      return nb;
    });
  };

  const bjAct = async (action) => {
    if (bjBusy || !bj || bj.status !== "active") return;
    sfx.keyTick();
    setBjBusy(true);
    if (demo) {
      const hand = { ...bj, player: [...bj.player], dealer: [...bj.dealer] };
      if (action === "double") {
        if (hand.player.length !== 2) { toast.error("Double down is only on the first two cards"); setBjBusy(false); return; }
        if (bank < hand.bet) { toast.error("Not enough in the bank to double"); setBjBusy(false); return; }
        hand.doubled = true; setBank((b) => b - hand.bet);
        hand.player.push(bjDeck.current.pop());
      } else if (action === "hit") {
        hand.player.push(bjDeck.current.pop());
      }
      hand.player_value = handVal(hand.player);
      if (action === "stand" || action === "double" || hand.player_value >= 21) {
        // dealer reveals + draws to 17
        const real = [hand.dealer[0], hand._dealerHole];
        while (handVal(real) < 17) real.push(bjDeck.current.pop());
        hand.dealer = real;
        hand._dealerExtra = [];
        const pv = handVal(hand.player), dv = handVal(real);
        const result = pv > 21 ? "lose" : dv > 21 || pv > dv ? "win" : pv === dv ? "push" : "lose";
        bjDemoSettle(hand, result);
        setBjBusy(false);
        return;
      }
      setBj({ ...hand });
      setBjBusy(false);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/${action}`, { hand_id: bj.id }, { params: { token } });
      bjApplyResult(r.data.hand);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBjBusy(false); }
  };

  /* ---- multiplayer tables (2 seats, shared dealer, face-up hands) ---- */
  const [mpTables, setMpTables] = useState({ open: [], mine: [] });
  const [mpTable, setMpTable] = useState(null);
  const [mpBet, setMpBet] = useState(50);
  const [mpBusy, setMpBusy] = useState(false);
  const [pc, setPc] = useState(null);
  const mpTableRef = useRef(null);
  const mpDeck = useRef([]);
  const setMp = (t) => { mpTableRef.current = t; setMpTable(t); };
  const mpMine = (t) => t?.seats?.find((s) => (demo ? s.playercode === 0 : s.playercode === pc)) || null;

  const mpAnnounce = (t) => {
    const me = mpMine(t);
    if (!me || t.status !== "done") return;
    if (me.result === "blackjack") { sfx.success(); toast.success(`BLACKJACK — ${fmt(me.payout)}`); }
    else if (me.result === "win") { sfx.success(); toast.success(`Winner — ${fmt(me.payout)}`); }
    else if (me.result === "push") toast.info("Push — stake returned");
    else toast.error(me.value > 21 ? `Bust at ${me.value} — house takes it` : "Dealer takes it");
    setRecent((rs) => [{ id: `mp-${Date.now()}-${Math.floor(Math.random() * 1e5)}`, kind: "blackjack_mp", result: me.result, payout: me.payout, bet: me.bet * (me.doubled ? 2 : 1), name }, ...rs].slice(0, 12));
  };

  const mpDemoSettleAll = (t) => {
    const dealer = [t.dealer[0], t._hole];
    while (handVal(dealer) < 17) dealer.push(mpDeck.current.pop());
    const dv = handVal(dealer);
    const dbj = dv === 21 && dealer.length === 2;
    const seats = t.seats.map((s) => {
      const pv = handVal(s.hand);
      const bet = s.bet * (s.doubled ? 2 : 1);
      let result = "lose", mult = 0;
      if (s.status === "busted") { result = "lose"; mult = 0; }
      else if (dbj) { result = s.natural ? "push" : "lose"; mult = s.natural ? 1 : 0; }
      else if (s.natural) { result = "blackjack"; mult = 2.5; }
      else if (dv > 21 || pv > dv) { result = "win"; mult = 2; }
      else if (pv === dv) { result = "push"; mult = 1; }
      return { ...s, status: "settled", result, payout: Math.floor(bet * mult), value: pv };
    });
    return { ...t, dealer, dealer_value: dv, seats, status: "done" };
  };

  const mpDemoBotStep = () => {
    const t = mpTableRef.current;
    if (!t || t.status !== "active") return;
    const bot = t.seats.find((s) => s.playercode === -1);
    if (bot && bot.status === "playing") {
      const hand = [...bot.hand];
      if (handVal(hand) < 16) hand.push(mpDeck.current.pop());
      const v = handVal(hand);
      const nb = { ...bot, hand, value: v, status: v > 21 ? "busted" : v >= 16 ? "stood" : "playing" };
      setMp({ ...t, seats: t.seats.map((s) => (s.playercode === -1 ? nb : s)) });
      if (nb.status === "playing") { later(mpDemoBotStep, 1200); return; }
    }
    mpDemoFinish();
  };

  const mpDemoFinish = (force = false) => {
    const t = mpTableRef.current;
    if (!t || t.status === "done") return;
    const me = mpMine(t);
    if (!me || me.status === "playing") return;
    if (!force && t.status === "open") { later(() => mpDemoFinish(), 1000); return; } // give the rival a moment to sit
    const bot = t.seats.find((s) => s.playercode === -1);
    if (bot && bot.status === "playing") { later(mpDemoBotStep, 900); return; }
    const done = mpDemoSettleAll(t);
    setMp(done);
    setBank((b) => b + (mpMine(done)?.payout || 0));
    mpAnnounce(done);
  };

  useEffect(() => {
    if (demo || game !== "tables") return;
    let dead = false;
    const tick = async () => {
      try {
        const r = await api.get(`/nc/servers/${serverId}/casino/blackjack/tables`, { params: { token } });
        if (dead) return;
        setMpTables(r.data);
        const cur = mpTableRef.current;
        if (cur) {
          const tr = await api.get(`/nc/servers/${serverId}/casino/blackjack/table/${cur.id}`, { params: { token } });
          if (dead) return;
          if (tr.data.table.status === "done" && cur.status !== "done") { mpAnnounce(tr.data.table); load(); }
          setMp(tr.data.table);
        } else if (r.data.mine?.length) {
          setMp(r.data.mine[0]); // re-open the table we were seated at
        }
      } catch { /* keep polling */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { dead = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, game, serverId, token]);

  const mpCreate = async () => {
    if (mpBusy || mpTable) return;
    if (mpBet > bank) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    setMpBusy(true);
    if (demo) {
      const R = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"], S = ["S", "H", "D", "C"];
      mpDeck.current = R.flatMap((r) => S.map((s) => r + s)).sort(() => Math.random() - 0.5);
      const d = mpDeck.current;
      const myHand = [d.pop(), d.pop()];
      const mv = handVal(myHand);
      const hole = d.pop();
      const seat = { playercode: 0, name: name || "You", bet: mpBet, hand: myHand, value: mv, doubled: false, natural: mv === 21, status: mv === 21 ? "stood" : "playing", result: null, payout: 0 };
      const t = { id: "demo-mp", room, status: "open", dealer: [d.pop(), "??"], _hole: hole, dealer_value: 0, seats: [seat] };
      setBank((b) => b - mpBet);
      setMp(t);
      later(() => { // a rival wanders over to the demo table
        const cur = mpTableRef.current;
        if (!cur || cur.status !== "open") return;
        const bHand = [mpDeck.current.pop(), mpDeck.current.pop()];
        const bv = handVal(bHand);
        const bot = { playercode: -1, name: "Dusty Callahan", bet: mpBet, hand: bHand, value: bv, doubled: false, natural: bv === 21, status: bv === 21 ? "stood" : "playing", result: null, payout: 0 };
        setMp({ ...cur, status: "active", seats: [...cur.seats, bot] });
        toast.info("Dusty Callahan sits opposite you");
        later(mpDemoBotStep, 1200);
      }, 2200);
      setMpBusy(false);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/table/create`, { bet: mpBet, room }, { params: { token } });
      setMp(r.data.table);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setMpBusy(false); }
  };

  const mpJoin = async (t) => {
    if (mpBusy || mpTable) return;
    if (mpBet > bank) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    setMpBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/table/join`, { table_id: t.id, bet: mpBet }, { params: { token } });
      setMp(r.data.table);
      toast.success(`You sit opposite ${t.host}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setMpBusy(false); }
  };

  const mpAct = async (move) => {
    const t = mpTableRef.current;
    if (mpBusy || !t || t.status === "done") return;
    const me = mpMine(t);
    if (!me || me.status !== "playing") return;
    sfx.keyTick();
    setMpBusy(true);
    if (demo) {
      const nt = { ...t, seats: t.seats.map((s) => ({ ...s, hand: [...s.hand] })) };
      const seat = mpMine(nt);
      if (move === "double") {
        if (seat.hand.length !== 2) { toast.error("Double down is only on the first two cards"); setMpBusy(false); return; }
        if (bank < seat.bet) { toast.error("Not enough in the bank to double"); setMpBusy(false); return; }
        seat.doubled = true; setBank((b) => b - seat.bet);
        seat.hand.push(mpDeck.current.pop());
      } else if (move === "hit") {
        seat.hand.push(mpDeck.current.pop());
      }
      seat.value = handVal(seat.hand);
      if (move === "stand" || move === "double" || seat.value >= 21) seat.status = seat.value > 21 ? "busted" : "stood";
      setMp(nt);
      setMpBusy(false);
      later(() => mpDemoFinish(), 700);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/table/${t.id}/action`, { move }, { params: { token } });
      setMp(r.data.table);
      if (r.data.table.status === "done") { mpAnnounce(r.data.table); load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setMpBusy(false); }
  };

  const mpSettleSolo = async () => {
    const t = mpTableRef.current;
    if (mpBusy || !t || t.status !== "open") return;
    setMpBusy(true);
    if (demo) { mpDemoFinish(true); setMpBusy(false); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/blackjack/table/${t.id}/settle`, {}, { params: { token } });
      setMp(r.data.table);
      if (r.data.table.status === "done") { mpAnnounce(r.data.table); load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setMpBusy(false); }
  };

  const mpLeave = async () => {
    const t = mpTableRef.current;
    if (mpBusy || !t || t.status !== "open") return;
    setMpBusy(true);
    if (demo) {
      const me = mpMine(t);
      setBank((b) => b + (me ? me.bet * (me.doubled ? 2 : 1) : 0));
      setMp(null);
      setMpBusy(false);
      return;
    }
    try {
      await api.post(`/nc/servers/${serverId}/casino/blackjack/table/${t.id}/leave`, {}, { params: { token } });
      setMp(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setMpBusy(false); }
  };

  const placeBet = (key) => {
    if (spinning) return;
    sfx.keyTick();
    setBets((b) => ({ ...b, [key]: (b[key] || 0) + chip }));
  };

  const demoSpinRoulette = () => {
    const result = Math.floor(Math.random() * 37);
    const winTypes = [];
    let payout = 0;
    for (const [key, amt] of Object.entries(bets)) {
      const win = (() => {
        if (key.startsWith("straight:")) return result === Number(key.split(":")[1]);
        if (key === "red") return REDS.has(result);
        if (key === "black") return result !== 0 && !REDS.has(result);
        if (key === "even") return result !== 0 && result % 2 === 0;
        if (key === "odd") return result % 2 === 1;
        if (key === "low") return result >= 1 && result <= 18;
        if (key === "high") return result >= 19;
        if (key.startsWith("dozen")) { const d = Number(key.slice(5)); return result >= (d - 1) * 12 + 1 && result <= d * 12; }
        if (key.startsWith("col")) { const c = Number(key.slice(3)); return result !== 0 && (result - c) % 3 === 0; }
        return false;
      })();
      if (win) { winTypes.push(key); payout += amt * (key.startsWith("straight") ? 36 : (key.startsWith("dozen") || key.startsWith("col") ? 3 : 2)); }
    }
    return { result, color: result === 0 ? "green" : REDS.has(result) ? "red" : "black", stake, payout, net: payout - stake, bank: bank - stake + payout, won_bets: winTypes };
  };

  const spinRoulette = async () => {
    if (spinning || stake <= 0) return;
    if (stake > bank) { toast.error("Not enough in the bank for that spread"); return; }
    sfx.click?.();
    setSpinning(true);
    setRouletteResult(null);
    setBallIn(false);
    let r;
    if (demo) {
      r = demoSpinRoulette();
    } else {
      try {
        const payload = { room, bets: Object.entries(bets).map(([key, amount]) => key.startsWith("straight:")
          ? { type: "straight", number: Number(key.split(":")[1]), amount }
          : { type: key, number: 0, amount }) };
        const res = await api.post(`/nc/servers/${serverId}/casino/roulette/spin`, payload, { params: { token } });
        r = res.data;
      } catch (e) { setSpinning(false); toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    }
    // animate: wheel ~6.2 turns decelerating, ball counter-spins then drops into the pocket
    const idx = WHEEL_ORDER.indexOf(r.result);
    const targetMod = (360 - idx * STEP + 360) % 360;
    setRotation((cur) => {
      const currentMod = ((cur % 360) + 360) % 360;
      const delta = (targetMod - currentMod + 360) % 360;
      return cur + 360 * 6 + delta;
    });
    setBallRot((cur) => {
      const currentMod = ((cur % 360) + 360) % 360;
      const delta = (0 - currentMod + 360) % 360;
      return cur - (360 * 7 + delta);
    });
    later(() => setBallIn(true), 3600);
    later(() => {
      setSpinning(false);
      setRouletteResult(r);
      setBank(r.bank);
      setBets({});
      setRecent((rs) => [{ id: `r-${Date.now()}-${Math.floor(Math.random() * 1e5)}`, kind: "roulette", result: String(r.result), payout: r.payout, bet: r.stake, name }, ...rs].slice(0, 12));
      if (r.payout > 0) { sfx.success(); toast.success(`${r.result} ${r.color.toUpperCase()} — won ${fmt(r.payout)}`); }
      else toast.error(`${r.result} ${r.color.toUpperCase()} — house takes it`);
      load();
    }, 4600);
  };

  const spinSlots = async () => {
    if (slotBusy) return;
    if (slotBet > bank) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    slotAudio.lever(); slotAudio.ratchet();
    setSlotBusy(true);
    setSlotWin(null);
    let r;
    if (demo) {
      const w = [2, 4, 8, 12, 16, 18];
      const pick = () => { const t = w.reduce((a, b) => a + b); let x = Math.random() * t; for (let i = 0; i < w.length; i++) { x -= w[i]; if (x < 0) return SLOT_SYMBOLS[i].id; } return "horseshoe"; };
      const rl = [pick(), pick(), pick()];
      const mult = rl[0] === rl[1] && rl[1] === rl[2] ? PAYTABLE.find(([id]) => id === rl[0])[1] : (rl[0] === rl[1] ? 2 : 0);
      r = { reels: rl, payout: slotBet * mult, net: slotBet * mult - slotBet, bank: bank - slotBet + slotBet * mult, win_kind: mult >= 5 ? "jackpot" : mult ? "pair" : "" };
    } else {
      try {
        const res = await api.post(`/nc/servers/${serverId}/casino/slots/spin`, { bet: slotBet, room }, { params: { token } });
        r = res.data;
      } catch (e) { setSlotBusy(false); toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    }
    // spin each reel ~5 symbol-lengths past, landing on the result; staggered stops
    const LEN = SLOT_SYMBOLS.length;
    setReelSpinning([true, true, true]);
    setReelPos((cur) => r.reels.map((sym, i) => {
      const targetIdx = SLOT_SYMBOLS.findIndex((s) => s.id === sym);
      const cycles = 4 + i; // reel 3 spins longest
      return cur[i] + cycles * LEN + ((targetIdx - (cur[i] % LEN)) + LEN) % LEN;
    }));
    r.reels.forEach((_, i) => later(() => {
      setReelSpinning((s) => s.map((v, j) => (j === i ? false : v)));
      slotAudio.reelStop();
    }, 1500 + i * 550));
    later(() => {
      setSlotBusy(false);
      setReels(r.reels);
      setBank(r.bank);
      setRecent((rs) => [{ id: `s-${Date.now()}-${Math.floor(Math.random() * 1e5)}`, kind: "slots", result: r.reels.join("-"), payout: r.payout, bet: slotBet, name }, ...rs].slice(0, 12));
      if (r.payout > 0) {
        if (r.win_kind === "jackpot") { slotAudio.jackpot(); slotAudio.coins(); } else slotAudio.win();
        setSlotWin(r);
        toast.success(r.win_kind === "jackpot" ? `JACKPOT — ${fmt(r.payout)}` : `Winner — ${fmt(r.payout)}`);
      }
      load();
    }, 3300);
  };

  const cellCls = "relative flex items-center justify-center font-mono font-bold cursor-pointer select-none border border-[#ffffff12] transition-transform hover:scale-105 hover:z-10";
  const chipOn = (key) => bets[key] > 0;

  return (
    <div data-testid="casino-page" className="fixed inset-0 overflow-hidden text-zinc-100 pointer-events-none" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="casino" />}
      <GamblingNotice />
      <div className="absolute inset-0 pointer-events-none" aria-hidden
        style={{ background: "radial-gradient(circle at 50% 18%, rgba(212,175,55,0.10), transparent 34%)" }} />
      {demo && (
        <div data-testid="casino-demo-backdrop" className="absolute inset-0 pointer-events-none" aria-hidden
          style={{ background: "radial-gradient(circle at 50% 18%, rgba(28,65,43,0.92), rgba(3,7,5,0.98) 58%), linear-gradient(135deg,#050807,#0b120d)" }} />
      )}
      {/* Full-floor CEF surface — transparent margins stay game world; tables use the whole viewport. */}
      <div data-testid="casino-floor-shell" className="absolute inset-0 pointer-events-auto flex flex-col">

      {/* header */}
      <header className="relative z-10 flex items-center gap-3 px-5 md:px-8 py-3.5 border-b border-[#d4af3722] flex-wrap"
        style={{ background: "linear-gradient(200deg, rgba(9,18,13,0.94), rgba(4,8,6,0.92))", backdropFilter: "blur(16px)" }}>
        <Spade className="w-6 h-6" style={{ color: GOLD }} />
        <div>
          <h1 className="font-black uppercase tracking-[0.3em] text-lg" style={{ color: GOLD }}>The Gilded Spade</h1>
          <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.25em]">{name || "Guest"} · tables close at dawn</p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <button data-testid="casino-room-toggle" onClick={() => { setRoom((r) => (r === "main" ? "high_roller" : "main")); setBj(null); sfx.keyTick(); }}
            className={`px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border transition-all ${room === "high_roller" ? "text-black" : "text-zinc-500 border-white/15 hover:border-white/40"}`}
            style={room === "high_roller" ? { backgroundColor: "#a78bfa", borderColor: "#a78bfa", boxShadow: "0 0 18px #a78bfa44" } : {}}>
            {room === "high_roller" ? "High-roller · $500 min" : "Main floor · $10 min"}
          </button>
          <div data-testid="casino-balance" className="flex items-center gap-2 border px-4 py-2" style={{ borderColor: `${GOLD}55`, backgroundColor: "#00000088" }}>
            <Coins className="w-4 h-4" style={{ color: GOLD }} />
            <span className="font-mono font-bold text-base">{bank == null ? "…" : fmt(bank)}</span>
          </div>
          {[["roulette", "Roulette"], ["slots", "Slots"], ["blackjack", "Blackjack"], ["tables", "Tables · 2P"]].map(([id, label]) => (
            <button key={id} data-testid={`casino-tab-${id}`} onClick={() => setGame(id)}
              className={`px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border transition-all ${game === id ? "text-black" : "text-zinc-400 border-white/15 hover:border-white/40"}`}
              style={game === id ? { backgroundColor: GOLD, borderColor: GOLD } : {}}>{label}</button>
          ))}
        </div>
      </header>

      <div className="relative z-10 flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        {/* ROULETTE */}
        {game === "roulette" && (
          <div className="min-h-full w-full max-w-[1480px] mx-auto flex flex-col xl:flex-row items-center justify-center gap-8 xl:gap-12 p-5 md:p-8 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {/* wheel */}
            <RouletteWheel rotation={rotation} ballRot={ballRot} ballIn={ballIn} spinning={spinning} rouletteResult={rouletteResult} />

            {/* betting layout */}
            <div className="flex-1 w-full max-w-3xl">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {roomChips.map((c) => (
                  <button key={c} data-testid={`chip-${c}`} onClick={() => { setChip(c); sfx.keyTick(); }}
                    className="w-11 h-11 rounded-full border-4 border-dashed font-mono text-[10px] font-bold transition-transform hover:scale-110"
                    style={{ borderColor: chip === c ? GOLD : "#ffffff33", backgroundColor: chip === c ? GOLD : "#111", color: chip === c ? "#000" : "#e4e4e7" }}>
                    {c}
                  </button>
                ))}
                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Stake <span data-testid="roulette-stake" className="text-zinc-100 font-bold">{fmt(stake)}</span></span>
                <button data-testid="roulette-clear" onClick={() => setBets({})} className="p-2 border border-white/15 text-zinc-400 hover:text-red-400 hover:border-red-500/50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>

              <div className="flex" style={{ backgroundColor: FELT, boxShadow: "inset 0 0 40px #00000088" }}>
                <button data-testid="bet-straight-0" onClick={() => placeBet("straight:0")}
                  className={`${cellCls} w-14 text-white`} style={{ backgroundColor: "#16a34a", minHeight: 168 }}>
                  0
                  {chipOn("straight:0") && <span className="absolute top-1 right-1 w-4 h-4 rounded-full text-[7px] flex items-center justify-center font-mono" style={{ backgroundColor: GOLD, color: "#000" }}>{bets["straight:0"]}</span>}
                </button>
                <div className="flex-1 grid grid-cols-12 gap-0">
                  {[3, 2, 1].map((rowOffset) => (
                    Array.from({ length: 12 }, (_, col) => rowOffset + col * 3).map((n) => {
                      const key = `straight:${n}`;
                      return (
                        <button key={n} data-testid={`bet-straight-${n}`} onClick={() => placeBet(key)}
                          className={`${cellCls} h-14 text-white text-sm`} style={{ backgroundColor: numColor(n) }}>
                          {n}
                          {chipOn(key) && <span data-testid={`betchip-${n}`} className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-4 px-0.5 rounded-full text-[7px] flex items-center justify-center font-mono z-10" style={{ backgroundColor: GOLD, color: "#000" }}>{bets[key]}</span>}
                        </button>
                      );
                    })
                  ))}
                </div>
              </div>

              {/* dozens + even money */}
              <div className="grid grid-cols-3 gap-1 mt-1">
                {[["dozen1", "1st 12"], ["dozen2", "2nd 12"], ["dozen3", "3rd 12"]].map(([k, l]) => (
                  <button key={k} data-testid={`bet-outside-${k}`} onClick={() => placeBet(k)} className={`${cellCls} h-9 text-[10px] uppercase tracking-[0.15em] text-zinc-200`} style={{ backgroundColor: "#0f1f18" }}>
                    {l}{chipOn(k) && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full text-[7px] flex items-center justify-center font-mono" style={{ backgroundColor: GOLD, color: "#000" }}>{bets[k]}</span>}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-6 gap-1 mt-1">
                {[["low", "1-18"], ["even", "Even"], ["red", "Red"], ["black", "Black"], ["odd", "Odd"], ["high", "19-36"]].map(([k, l]) => (
                  <button key={k} data-testid={`bet-outside-${k}`} onClick={() => placeBet(k)} className={`${cellCls} h-9 text-[10px] uppercase tracking-[0.12em]`}
                    style={{ backgroundColor: k === "red" ? "#7f1d1d" : k === "black" ? "#111" : "#0f1f18", color: "#e4e4e7" }}>
                    {l}{chipOn(k) && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full text-[7px] flex items-center justify-center font-mono" style={{ backgroundColor: GOLD, color: "#000" }}>{bets[k]}</span>}
                  </button>
                ))}
              </div>

              <button data-testid="roulette-spin" disabled={spinning || stake <= 0 || stake > (bank ?? 0)} onClick={spinRoulette}
                className="mt-4 w-full py-4 font-mono text-xs uppercase tracking-[0.4em] font-bold text-black disabled:opacity-40 transition-all hover:tracking-[0.5em] flex items-center justify-center gap-3"
                style={{ backgroundColor: GOLD, boxShadow: `0 0 30px ${GOLD}44` }}>
                <RotateCw className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} /> {spinning ? "No more bets…" : "Spin"}
              </button>
            </div>
          </div>
        )}

        {/* BLACKJACK */}
        {game === "blackjack" && (
          <div data-testid="blackjack-table" className="flex-1 flex flex-col items-center justify-center gap-5 p-6">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">{ROOM_LABEL[room]} · dealer stands on 17 · blackjack pays 3:2</p>

            {/* dealer */}
            <div className="flex flex-col items-center gap-2">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Dealer {bj && <span className="text-zinc-300">· {bj.status === "settled" ? bj.dealer_value : bj.dealer_value}</span>}</p>
              <div data-testid="bj-dealer" className="flex gap-2 min-h-20">
                {bj ? bj.dealer.map((c, i) => <Card key={`d-${i}`} code={c} i={i} />) : <div className="w-14 h-20 rounded-md border border-dashed border-white/15" />}
              </div>
            </div>

            {/* felt divider */}
            <div className="w-full max-w-md h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />

            {/* player */}
            <div className="flex flex-col items-center gap-2">
              <div data-testid="bj-player" className="flex gap-2 min-h-20">
                {bj ? bj.player.map((c, i) => <Card key={`p-${i}`} code={c} i={i} />) : <div className="w-14 h-20 rounded-md border border-dashed border-white/15" />}
              </div>
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">You {bj && <span className="text-zinc-100 font-bold text-xs">{bj.player_value}</span>}</p>
            </div>

            {/* result banner */}
            {bj?.status === "settled" && (
              <p data-testid="bj-result" className="font-mono text-[11px] uppercase tracking-[0.3em] px-6 py-2 border"
                style={{
                  color: bj.result === "lose" ? "#ef4444" : bj.result === "push" ? "#a1a1aa" : "#34d399",
                  borderColor: bj.result === "lose" ? "#ef444455" : bj.result === "push" ? "#ffffff22" : "#34d39955",
                  backgroundColor: "#00000099", animation: "ncFadeUp 0.3s ease both",
                }}>
                {bj.result === "blackjack" ? `Blackjack — ${fmt(bj.payout)}` : bj.result === "win" ? `Winner — ${fmt(bj.payout)}` : bj.result === "push" ? "Push — stake returned" : bj.player_value > 21 ? "Bust — house wins" : "Dealer wins"}
              </p>
            )}

            {/* controls */}
            {!bj || bj.status === "settled" ? (
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  {roomChips.map((c) => (
                    <button key={c} data-testid={`bj-bet-${c}`} onClick={() => setBjBet(c)}
                      className={`px-3 py-2 font-mono text-[10px] border transition-colors ${bjBet === c ? "text-black" : "text-zinc-400 border-white/15"}`}
                      style={bjBet === c ? { backgroundColor: GOLD, borderColor: GOLD } : {}}>{c}</button>
                  ))}
                </div>
                <button data-testid="bj-deal" disabled={bjBusy || bjBet > (bank ?? 0)} onClick={bjDeal}
                  className="px-8 py-3 font-mono text-[11px] uppercase tracking-[0.3em] font-bold text-black disabled:opacity-40"
                  style={{ backgroundColor: GOLD, boxShadow: `0 0 24px ${GOLD}44` }}>
                  Deal {fmt(bjBet)}
                </button>
              </div>
            ) : (
              <div className="flex gap-2.5">
                <button data-testid="bj-hit" disabled={bjBusy} onClick={() => bjAct("hit")}
                  className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: GOLD }}>Hit</button>
                <button data-testid="bj-stand" disabled={bjBusy} onClick={() => bjAct("stand")}
                  className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border border-white/25 hover:border-white/60 transition-colors disabled:opacity-40">Stand</button>
                <button data-testid="bj-double" disabled={bjBusy || bj.player.length !== 2 || (bank ?? 0) < bj.bet} onClick={() => bjAct("double")}
                  className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border border-[#a78bfa66] text-[#a78bfa] hover:bg-[#a78bfa15] transition-colors disabled:opacity-40">Double</button>
              </div>
            )}
          </div>
        )}

        {/* MULTIPLAYER TABLES — 2 seats, shared dealer, face-up hands */}
        {game === "tables" && (
          <div data-testid="mp-tables" className="flex-1 flex flex-col items-center gap-5 p-6 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">{ROOM_LABEL[room]} · two seats · shared dealer · every hand face-up</p>

            {!mpTable && (
              <div className="w-full max-w-2xl flex flex-col gap-2">
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Open tables</p>
                {mpTables.open.length === 0 && (
                  <p data-testid="mp-no-tables" className="font-mono text-[10px] text-zinc-600 border border-dashed border-white/10 px-4 py-6 text-center">
                    {demo ? "Demo floor — open a table and a local rival wanders over." : "No open tables on the floor — open one and a rival can sit opposite you."}
                  </p>
                )}
                {mpTables.open.map((t) => (
                  <div key={t.id} data-testid={`mp-open-${t.id.slice(0, 8)}`} className="border border-white/10 bg-black/40 px-4 py-3 flex items-center gap-4">
                    <Spade className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[11px] text-zinc-100 truncate">{t.host}</p>
                      <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">in for {fmt(t.host_bet)} · waiting for a rival</p>
                    </div>
                    <button data-testid={`mp-join-${t.id.slice(0, 8)}`} disabled={mpBusy} onClick={() => mpJoin(t)}
                      className="shrink-0 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40"
                      style={{ backgroundColor: GOLD }}>Sit down · {fmt(mpBet)}</button>
                  </div>
                ))}
                <div className="flex items-center gap-3 mt-3 justify-center flex-wrap">
                  <div className="flex gap-1.5">
                    {roomChips.map((c) => (
                      <button key={c} data-testid={`mp-bet-${c}`} onClick={() => setMpBet(c)}
                        className={`px-3 py-2 font-mono text-[10px] border transition-colors ${mpBet === c ? "text-black" : "text-zinc-400 border-white/15"}`}
                        style={mpBet === c ? { backgroundColor: GOLD, borderColor: GOLD } : {}}>{c}</button>
                    ))}
                  </div>
                  <button data-testid="mp-create" disabled={mpBusy || mpBet > (bank ?? 0)} onClick={mpCreate}
                    className="px-8 py-3 font-mono text-[11px] uppercase tracking-[0.3em] font-bold text-black disabled:opacity-40"
                    style={{ backgroundColor: GOLD, boxShadow: `0 0 24px ${GOLD}44` }}>
                    Open table · {fmt(mpBet)}
                  </button>
                </div>
              </div>
            )}

            {mpTable && (
              <>
                {/* shared dealer */}
                <div className="flex flex-col items-center gap-2">
                  <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Dealer {mpTable.status === "done" && <span className="text-zinc-300">· {mpTable.dealer_value}</span>}</p>
                  <div data-testid="mp-dealer" className="flex gap-2 min-h-20">
                    {mpTable.dealer.map((c, i) => <Card key={`md-${i}`} code={c} i={i} />)}
                  </div>
                </div>

                <div className="w-full max-w-2xl h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />

                {/* seats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                  {mpTable.seats.map((s) => {
                    const isMe = demo ? s.playercode === 0 : s.playercode === pc;
                    const chipLabel = { playing: "thinking…", stood: "stands", busted: "bust", settled: s.result || "done" }[s.status];
                    const chipCls = s.status === "busted" ? "text-red-400 border-red-500/40" : s.status === "playing" ? "text-amber-400 border-amber-500/40 animate-pulse" : "text-zinc-400 border-white/15";
                    return (
                      <div key={s.playercode} data-testid={isMe ? "mp-seat-me" : "mp-seat-rival"}
                        className={`border p-4 flex flex-col items-center gap-2 ${isMe ? "border-[#d4af3766] bg-[#d4af3708]" : "border-white/10 bg-black/40"}`}>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                          {isMe ? "You" : s.name}
                          <span className={`px-1.5 py-0.5 border text-[8px] uppercase ${chipCls}`}>{chipLabel}</span>
                        </p>
                        <div className="flex gap-2 min-h-20 flex-wrap justify-center">
                          {s.hand.map((c, i) => <Card key={`s-${s.playercode}-${i}`} code={c} i={i} />)}
                        </div>
                        <p className="font-mono text-xs text-zinc-200 font-bold">{s.value}{s.doubled && <span className="text-[#a78bfa] text-[8px] uppercase ml-2">doubled</span>}</p>
                        {mpTable.status === "done" ? (
                          <p data-testid={isMe ? "mp-result-me" : "mp-result-rival"} className="font-mono text-[9px] uppercase tracking-[0.2em]"
                            style={{ color: s.result === "lose" ? "#ef4444" : s.result === "push" ? "#a1a1aa" : "#34d399" }}>
                            {s.result === "blackjack" ? `Blackjack +${fmt(s.payout)}` : s.result === "win" ? `Win +${fmt(s.payout)}` : s.result === "push" ? "Push — stake back" : "Lost the stake"}
                          </p>
                        ) : (
                          <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">stake {fmt(s.bet * (s.doubled ? 2 : 1))}</p>
                        )}
                      </div>
                    );
                  })}
                  {mpTable.seats.length === 1 && mpTable.status !== "done" && (
                    <div className="border border-dashed border-white/15 p-4 flex flex-col items-center justify-center gap-2 min-h-40">
                      <p data-testid="mp-waiting" className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 animate-pulse">Waiting for a rival to sit…</p>
                      <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">anyone on the floor can take this seat</p>
                    </div>
                  )}
                </div>

                {/* controls */}
                {mpTable.status !== "done" ? (
                  <div className="flex gap-2.5 flex-wrap justify-center">
                    {mpMine(mpTable)?.status === "playing" && (
                      <>
                        <button data-testid="mp-hit" disabled={mpBusy} onClick={() => mpAct("hit")}
                          className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: GOLD }}>Hit</button>
                        <button data-testid="mp-stand" disabled={mpBusy} onClick={() => mpAct("stand")}
                          className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border border-white/25 hover:border-white/60 transition-colors disabled:opacity-40">Stand</button>
                        <button data-testid="mp-double" disabled={mpBusy || (mpMine(mpTable)?.hand?.length ?? 0) !== 2 || (bank ?? 0) < (mpMine(mpTable)?.bet ?? 0)} onClick={() => mpAct("double")}
                          className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border border-[#a78bfa66] text-[#a78bfa] hover:bg-[#a78bfa15] transition-colors disabled:opacity-40">Double</button>
                      </>
                    )}
                    {mpTable.status === "open" && mpMine(mpTable)?.status !== "playing" && (
                      <button data-testid="mp-settle-solo" disabled={mpBusy} onClick={mpSettleSolo}
                        className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: GOLD }}>Settle vs dealer</button>
                    )}
                    {mpTable.status === "open" && (
                      <button data-testid="mp-leave" disabled={mpBusy} onClick={mpLeave}
                        className="px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">Leave table</button>
                    )}
                    {mpTable.status === "active" && mpMine(mpTable)?.status !== "playing" && (
                      <p data-testid="mp-wait-finish" className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 animate-pulse self-center">Your hand is done — sweating the other seat…</p>
                    )}
                  </div>
                ) : (
                  <button data-testid="mp-new-table" onClick={() => setMp(null)}
                    className="px-8 py-3 font-mono text-[11px] uppercase tracking-[0.3em] font-bold text-black" style={{ backgroundColor: GOLD, boxShadow: `0 0 24px ${GOLD}44` }}>New table</button>
                )}
              </>
            )}
          </div>
        )}

        {/* SLOTS — presentation extracted; state and settlement stay here. */}
        {game === "slots" && (
          <div className="flex-1 flex flex-col items-center gap-5 p-5">
            <SlotsCabinet jackpotFlash={jackpotFlash} reels={reels} reelPos={reelPos} reelSpinning={reelSpinning}
                          slotWin={slotWin} slotBusy={slotBusy} payoutCount={payoutCount} bank={bank}
                          slotBet={slotBet} setSlotBet={setSlotBet} leverPulled={leverPulled} setLeverPulled={setLeverPulled}
                          spinSlots={spinSlots} roomChips={roomChips} later={later} />
          </div>
        )}
      </div>{/* end scroll wrapper */}

      {/* recent strip */}
      <div data-testid="casino-recent" className="shrink-0 border-t border-[#ffffff0d] px-4 md:px-8 py-2 flex items-center gap-2 overflow-x-auto"
        style={{ scrollbarWidth: "thin", background: "rgba(3,7,5,0.88)", backdropFilter: "blur(14px)" }}>
        <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1.5 shrink-0"><History className="w-3 h-3" /> Last</p>
        {recent.length === 0 && <p className="font-mono text-[9px] text-zinc-600">No action yet — the floor is watching.</p>}
        {recent.map((r) => (
          <div key={r.id} data-testid={`recent-${r.id.slice(0, 10)}`} className="border border-white/10 bg-black/40 px-2.5 py-1.5 flex items-center gap-2 shrink-0">
            {r.kind === "roulette"
              ? <span className="w-6 h-6 rounded-full flex items-center justify-center font-mono text-[10px] font-bold text-white" style={{ backgroundColor: numColor(Number(r.result)) }}>{r.result}</span>
              : <span className="font-mono text-[8px] uppercase text-zinc-300 truncate max-w-24">{r.result}</span>}
            <span className={`ml-auto font-mono text-[9px] ${r.payout > 0 ? "text-emerald-400" : "text-zinc-500"}`}>{r.payout > 0 ? `+${fmt(r.payout)}` : `-${fmt(r.bet)}`}</span>
          </div>
        ))}
      </div>
      </div>{/* end floor shell */}

      <style>{`
        @keyframes ncFadeUp { from { opacity: 0; transform: translate(-0%, 8px); } to { opacity: 1; transform: translate(0, 0); } }
        @keyframes ncBulb { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
        @keyframes ncCabinetStrobe { 0%, 100% { box-shadow: 0 0 60px #000000cc, inset 0 2px 0 #d4af3755; } 50% { box-shadow: 0 0 110px #d4af37bb, inset 0 2px 0 #d4af37; } }
        @keyframes ncPaylinePulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
        @keyframes ncCoinFall { 0% { transform: translateY(-8px) rotate(0deg); opacity: 0; } 15% { opacity: 1; } 100% { transform: translateY(34px) rotate(240deg); opacity: 0; } }
      `}</style>
    </div>
  );
}
